import { useState } from "react";
import React, { Fragment } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, Paper } from "@mui/material";
import { styled } from "@mui/system";
import Home from "./components/Home";
import Swipe from "./components/Swipe";
import Match from "./components/Match";
import Setup from "./components/Setup";
import Lobby from "./components/Lobby";
import JoinSessionByCode from "./components/JoinSessionByCode";
import { PostHogProvider } from "./Posthogprovider.jsx";
import HowItWorks from "./components/HowItWorks.js";
import NotFound from "./components/NotFound.js";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Feedback from "./components/Feedback.js";

const AppBackground = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 40%, #FEF9C3 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  // On small screens, let the content stretch to full width/height
  "@media (max-width: 500px)": {
    alignItems: "stretch",
    justifyContent: "stretch",
  },
}));

const AppFrame = styled(Paper)(({ theme, $allowScroll }) => ({
  width: "100%",
  maxWidth: 440,
  height: $allowScroll ? "auto" : 812,
  minHeight: $allowScroll ? 812 : "auto",
  maxHeight: $allowScroll ? "90vh" : 812,
  borderRadius: theme.spacing(3),
  overflow: $allowScroll ? "auto" : "hidden",
  boxShadow: theme.shadows[12],
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease",
  margin: theme.spacing(2),

  "@media (max-width: 500px)": {
    maxWidth: "100%",
    borderRadius: 0,
    boxShadow: "none",
    height: $allowScroll ? "auto" : "100vh",
    minHeight: "100vh",
    maxHeight: "none",
    margin: 0,
  },
}));

function AppContent({ sessionInfo, setSessionInfo }) {
  const location = useLocation();

  // Pages that need natural scrolling (not fixed height with internal scroll)
  const scrollablePages = [
    "/",
    "/how-it-works",
    "/setup",
    "/privacy-policy",
    "/feedback",
  ];
  const isYourRestaurant = location.pathname.includes("/your-restaurant");
  const allowScroll =
    scrollablePages.includes(location.pathname) || isYourRestaurant;

  return (
    <AppFrame elevation={12} $allowScroll={allowScroll}>
      <Routes>
        <Route exact path="/" element={<Home />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route
          exact
          path="/setup"
          element={
            <Setup sessionInfo={sessionInfo} setSessionInfo={setSessionInfo} />
          }
        />
        <Route path="/join/:joinCode" element={<JoinSessionByCode />} />
        <Route path="/session/:sessionId">
          <Route path="lobby" element={<Lobby sessionInfo={sessionInfo} />} />
          <Route path="match" element={<Swipe sessionInfo={sessionInfo} />} />
          <Route path="your-restaurant" element={<Match />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppFrame>
  );
}

function App() {
  const [sessionInfo, setSessionInfo] = useState({});

  const theme = createTheme({
    typography: {
      fontFamily: '"Lato", sans-serif',
      secondFontFamily: '"Lalezar", cursive',
    },
  });

  return (
    <PostHogProvider>
      <AppBackground>
        <ThemeProvider theme={theme}>
          <Router>
            <AppContent
              sessionInfo={sessionInfo}
              setSessionInfo={setSessionInfo}
            />
          </Router>
        </ThemeProvider>
      </AppBackground>
    </PostHogProvider>
  );
}

export default App;
